import { Button, Grid } from '@mui/material';
import type React from 'react';
import { useEffect, useState } from 'react';
import { templates } from '../../../lib/proposal-templates';
import type { ProposalHeading } from '../../../types/proposal';

const EMPTY_ARRAY: never[] = [];

interface TemplateSelectorProps {
  effectiveFormatMode: 'narrative' | 'hybrid';
  canLoadExtractedStructure: boolean;
  canLoadHybridExtractedStructure: boolean;
  onLoadExtractedStructure?: ((mode: 'standard' | 'hybrid') => void) | undefined;
  savedStructureTemplate?: ProposalHeading[] | undefined;
  setProposalStructure: (items: ProposalHeading[]) => void;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  effectiveFormatMode,
  canLoadExtractedStructure,
  canLoadHybridExtractedStructure,
  onLoadExtractedStructure,
  savedStructureTemplate,
  setProposalStructure,
}) => {
  // Set default active based on effectiveFormatMode.
  const [activeButton, setActiveButton] = useState<string>(effectiveFormatMode);

  useEffect(() => {
    setActiveButton(effectiveFormatMode);
  }, [effectiveFormatMode]);

  const getButtonProps = (id: string) => {
    const isActive = activeButton === id;
    return {
      variant: isActive ? ('contained' as const) : ('outlined' as const),
      fullWidth: true,
      // biome-ignore lint/suspicious/noExplicitAny: Theme types
      sx: (theme: any) => ({
        ...(isActive
          ? {
              backgroundColor: theme.palette.bidlogiq.brick,
              color: 'bidlogiq.white',
              '&:hover': {
                backgroundColor: theme.palette.bidlogiq.brick,
                opacity: 0.9,
              },
            }
          : {
              backgroundColor: theme.palette.greyLight,
              color: theme.palette.text.primary,
              borderColor: theme.palette.bidlogiq.brick,
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[400],
                borderColor: theme.palette.bidlogiq.brick,
              },
            }),
        fontSize: '0.8rem',
        height: '100%',
      }),
    };
  };

  return (
    <Grid container spacing={1.5} sx={{ mb: 2 }}>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Button
          onClick={() => {
            setActiveButton('saved');
            setProposalStructure(savedStructureTemplate || templates[0]?.structure || EMPTY_ARRAY);
          }}
          {...getButtonProps('saved')}
        >
          Saved Outline
        </Button>
      </Grid>
      {onLoadExtractedStructure && (
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Button
            onClick={() => {
              setActiveButton('narrative');
              onLoadExtractedStructure('standard');
            }}
            disabled={!canLoadExtractedStructure}
            {...getButtonProps('narrative')}
          >
            Extracted (Full)
          </Button>
        </Grid>
      )}
      {onLoadExtractedStructure && (
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Button
            onClick={() => {
              setActiveButton('hybrid');
              onLoadExtractedStructure('hybrid');
            }}
            disabled={!canLoadHybridExtractedStructure}
            {...getButtonProps('hybrid')}
          >
            Extracted (Exclude Forms)
          </Button>
        </Grid>
      )}
      {templates.map((template) => (
        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={template.name}>
          <Button
            onClick={() => {
              setActiveButton(template.name);
              setProposalStructure(template.structure);
            }}
            {...getButtonProps(template.name)}
          >
            {template.name}
          </Button>
        </Grid>
      ))}
    </Grid>
  );
};

export default TemplateSelector;
